import React from "react";
import { Link } from "react-router-dom";
import { YCN_LOGO_URL, YCN_FOUNDERS_IMAGE, ASHLEY_PORTRAIT, JEMMALYN_PORTRAIT } from "@/data/constants";
import { Sparkles, Trophy, Users, Heart, Film, Mic, ArrowRight, Play } from "lucide-react";
import SEO from "@/components/seo/SEO";

const FOUNDER_BIOS = [
  {
    name: "Jemmalyn Hewlett",
    role: "Founder",
    testId: "founder-jemmalyn",
    portrait: JEMMALYN_PORTRAIT,
    bio: "Jemmalyn Hewlett is the Founder of Your Channel Network, a visionary entrepreneur and media leader committed to creating platforms that entertain, inspire, and open doors for others.\n\nHer work spans entrepreneurship, television production, community impact, and media. As the founder and producer behind Poolside Cooking and the visionary behind Your Channel Network, Jemmalyn has built opportunities for entertainers, entrepreneurs, creators, and community voices to share their stories with wider audiences.\n\nHer vision for YCN is rooted in ownership, opportunity, and creating a lasting platform where diverse stories and independent voices can be seen and heard.",
  },
  {
    name: "Ashley Marie Lewis",
    role: "Co-Founder",
    testId: "cofounder-ashley",
    portrait: ASHLEY_PORTRAIT,
    bio: "Ashley Marie Lewis is the Co-Founder of Your Channel Network and a television host, entrepreneur, and creative leader helping shape YCN's voice, audience experience, and future.\n\nAs the host of Poolside Cooking, Ashley has interviewed entertainers, artists, entrepreneurs, and influential voices while bringing warmth, energy, and authentic conversation to the screen.\n\nAt YCN, she helps lead the network's creative direction, brand development, partnerships, programming vision, and audience engagement as the platform grows its reach.",
  },
];

const PILLARS = [
  { icon: Film, title: "Entertainment", body: "Movies, series, original programming, comedy, lifestyle, and unforgettable viewing experiences." },
  { icon: Mic, title: "Storytelling", body: "Authentic voices and powerful stories that connect with audiences." },
  { icon: Sparkles, title: "Opportunity", body: "A growing platform where independent creators and emerging voices can find visibility." },
  { icon: Trophy, title: "Legacy", body: "Building ownership, opportunity, and a media platform designed to grow for generations." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-24 md:pt-28">
      <SEO
        title="About YCN"
        description="Your Channel Network is a Virginia Beach-based, woman-owned, mother-and-daughter-led streaming network. Meet founders Jemmalyn Hewlett and Ashley Marie Lewis, and discover the vision behind YCN and Poolside Cooking."
        path="/about"
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[#050505]" />
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(55% 65% at 18% 30%, rgba(124,58,237,0.42), transparent 60%), radial-gradient(50% 55% at 85% 72%, rgba(249,115,22,0.36), transparent 60%), radial-gradient(38% 40% at 55% 50%, rgba(59,130,246,0.28), transparent 60%)" }} />
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)", backgroundSize: "3px 3px" }} />
        <div className="relative max-w-[1500px] mx-auto px-6 md:px-12 py-16 md:py-24 grid lg:grid-cols-[1fr,1.05fr] gap-10 items-start">
          <div>
            <div className="text-[11px] uppercase tracking-[0.4em] text-white/60 mb-3">About Your Channel Network</div>
            <h1 className="font-display text-5xl md:text-7xl font-black uppercase leading-[0.95] tracking-tight">
              Our Story. <span className="ycn-gradient-text">Our Vision.</span> Your Channel.
            </h1>
            <p className="mt-6 text-white/80 text-lg max-w-xl leading-relaxed">
              Your Channel Network is a global, independently owned, woman-owned streaming network created to entertain, inspire, and empower through storytelling, culture, creativity, and opportunity.
            </p>
          </div>
          <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-30px_rgba(124,58,237,0.5)]" data-testid="about-hero-founders-image">
            <div className="absolute -inset-6 -z-10 blur-3xl opacity-40 bg-gradient-to-tr from-violet-700 via-blue-600 to-orange-500 rounded-full" />
            <img src={YCN_FOUNDERS_IMAGE} alt="YCN Founders Jemmalyn Hewlett and Ashley Marie Lewis" className="w-full block object-cover" />
          </div>
        </div>
      </section>

      {/* OUR STORY section removed per client request */}

      {/* LEADERSHIP */}
      <section className="px-6 md:px-12 py-20 border-t border-white/5" data-testid="about-leadership">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-[11px] uppercase tracking-[0.35em] text-white/60 mb-3">Leadership</div>
          <h2 className="font-display text-4xl md:text-6xl font-black leading-tight">
            Meet the <span className="ycn-gradient-text">Women Behind YCN</span>
          </h2>

          {/* Profiles */}
          <div className="mt-12 grid md:grid-cols-2 gap-6" data-testid="about-founder-profiles">
            {FOUNDER_BIOS.map((f) => (
              <div key={f.name} data-testid={f.testId} className="ycn-glass rounded-3xl p-8 md:p-10 relative overflow-hidden group">
                <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full blur-3xl opacity-20 bg-gradient-to-br from-violet-500 via-blue-500 to-orange-500 group-hover:opacity-30 transition-opacity" />
                {f.portrait && (
                  <div className="relative float-right ml-5 mb-3 w-28 h-28 md:w-36 md:h-36 rounded-2xl overflow-hidden border border-white/15 shadow-xl">
                    <img src={f.portrait} alt={f.name} className="w-full h-full object-cover object-top" />
                  </div>
                )}
                <div className="relative">
                  <div className="text-[10px] uppercase tracking-[0.35em] text-white/50">{f.role}</div>
                  <div className="font-display text-3xl md:text-4xl font-black mt-1 tracking-tight">{f.name}</div>
                  <div className="mt-5 space-y-4 text-white/75 text-[15px] leading-relaxed whitespace-pre-line">
                    {f.bio}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TWO GENERATIONS */}
      <section className="px-6 md:px-12 py-20 border-t border-white/5" data-testid="about-two-generations">
        <div className="max-w-[1200px] mx-auto text-center">
          <div className="text-[11px] uppercase tracking-[0.35em] text-white/60 mb-3">Mother · Daughter · Vision</div>
          <h2 className="font-display text-4xl md:text-6xl font-black leading-tight">
            Two Generations. <span className="ycn-gradient-text">One Vision.</span>
          </h2>
          <div className="mt-8 space-y-5 text-white/80 text-lg leading-relaxed max-w-3xl mx-auto">
            <p>At the heart of Your Channel Network is a mother-and-daughter partnership built on vision, creativity, determination, and a shared belief in the power of ownership.</p>
            <p>Jemmalyn Hewlett and Ashley Marie Lewis bring different perspectives, experiences, and strengths to YCN while sharing one mission: to build a platform where entertainment, culture, creators, and meaningful stories have room to grow.</p>
            <p>Together, they are building more than a streaming network. They are building a platform designed for legacy, opportunity, and the next generation of storytellers.</p>
          </div>
        </div>
      </section>

      {/* MISSION / VISION */}
      <section className="px-6 md:px-12 py-20 border-t border-white/5" data-testid="about-mission-vision">
        <div className="max-w-[1300px] mx-auto grid md:grid-cols-2 gap-6">
          <div className="relative rounded-3xl p-8 md:p-10 overflow-hidden ycn-glass">
            <div className="absolute inset-0 rounded-3xl p-px pointer-events-none" style={{ background: "linear-gradient(135deg, rgba(147,51,234,0.5), rgba(59,130,246,0.3), rgba(249,115,22,0.4))", WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)", WebkitMaskComposite: "xor", maskComposite: "exclude" }} />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.35em] text-white/50">Our Mission</div>
              <h3 className="font-display text-2xl md:text-3xl font-black mt-2">Entertain. Inspire. Empower.</h3>
              <p className="mt-4 text-white/75 leading-relaxed">
                To entertain, inspire, and empower audiences by creating a global platform for compelling entertainment, original programming, independent creators, and stories that deserve to be seen and heard.
              </p>
            </div>
          </div>
          <div className="relative rounded-3xl p-8 md:p-10 overflow-hidden ycn-glass">
            <div className="absolute inset-0 rounded-3xl p-px pointer-events-none" style={{ background: "linear-gradient(135deg, rgba(59,130,246,0.5), rgba(147,51,234,0.3), rgba(249,115,22,0.4))", WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)", WebkitMaskComposite: "xor", maskComposite: "exclude" }} />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.35em] text-white/50">Our Vision</div>
              <h3 className="font-display text-2xl md:text-3xl font-black mt-2">A globally recognized destination.</h3>
              <p className="mt-4 text-white/75 leading-relaxed">
                To build Your Channel Network into a globally recognized entertainment destination where audiences discover unforgettable content and creators find meaningful opportunities to reach the world.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS section removed per client request */}

      {/* POOLSIDE CONNECTION */}
      <section className="px-6 md:px-12 py-20 border-t border-white/5" data-testid="about-poolside">
        <div className="max-w-[1400px] mx-auto relative overflow-hidden rounded-3xl border border-white/10">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0E7490] via-[#0B0B0F] to-[#EA580C]" />
          <div className="absolute inset-0 opacity-55" style={{ backgroundImage: "radial-gradient(circle at 22% 30%, rgba(6,182,212,0.45), transparent 55%), radial-gradient(circle at 80% 70%, rgba(249,115,22,0.45), transparent 55%)" }} />
          <div className="relative p-10 md:p-16 grid md:grid-cols-[1.2fr,0.8fr] gap-10 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.35em] text-white/70 mb-3">From Poolside to a Global Platform</div>
              <h2 className="font-display text-4xl md:text-5xl font-black leading-tight">
                Where <span className="ycn-gradient-text">culture, food & conversation</span> meet.
              </h2>
              <div className="mt-6 space-y-4 text-white/85 leading-relaxed max-w-xl">
                <p>Poolside Cooking helped demonstrate the power of combining food, entertainment, culture, and authentic conversation. Through interviews, celebrity guests, memorable meals, and real stories, the show created connections that extended beyond the kitchen.</p>
                <p>Today, Poolside Cooking is part of the growing programming experience available through Your Channel Network.</p>
              </div>
              <Link to="/title/poolside-cooking" data-testid="about-poolside-cta" className="mt-8 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white text-black font-bold hover:bg-white/90 transition">
                <Play size={16} fill="currentColor" /> Watch Poolside Cooking
              </Link>
            </div>
            <div className="hidden md:block">
              <div className="rounded-2xl overflow-hidden aspect-[3/4] max-w-[300px] ml-auto border border-white/15 shadow-2xl">
                <img src="/covers/poolside-cooking.jpg" alt="Poolside Cooking" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 md:px-12 py-20 border-t border-white/5" data-testid="about-final-cta">
        <div className="max-w-[1200px] mx-auto ycn-glass rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full blur-3xl opacity-30 bg-violet-600" />
          <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full blur-3xl opacity-30 bg-orange-500" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-64 w-64 rounded-full blur-3xl opacity-20 bg-blue-500" />
          <div className="relative">
            <h2 className="font-display text-4xl md:text-6xl font-black leading-[1.05] tracking-tight uppercase">
              The next great story <span className="ycn-gradient-text">could be yours.</span>
            </h2>
            <p className="mt-5 text-white/75 max-w-xl mx-auto text-lg">Watch. Discover. Create. Connect with Your Channel Network.</p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/" data-testid="about-cta-start" className="ycn-btn-primary inline-flex items-center gap-2 px-7 py-3 rounded-full font-bold text-white transition-all">
                Start Watching <ArrowRight size={16} />
              </Link>
              <Link to="/browse" data-testid="about-cta-explore" className="inline-flex items-center gap-2 px-7 py-3 rounded-full ycn-glass hover:bg-white/15 font-semibold text-white transition">
                Explore YCN
              </Link>
              <Link to="/submit" data-testid="about-cta-submissions" className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-white/20 hover:bg-white/10 font-semibold text-white transition">
                Creator Submissions — Coming Soon
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
